import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useCurrentCalculatorSegments } from '../composables/useCurrentCalculatorSegments';
import { useCurrentCalculation } from '../composables/useCurrentCalculation';

import { useCalculationsStore } from '../store/calculations';

export function useSegmentSelection() {
  const { unrequiredSegments } = useCurrentCalculatorSegments();
  const { currentCalculation } = useCurrentCalculation();

  const route = useRoute();
  const router = useRouter();
  const calculationStore = useCalculationsStore();

  const selectedSegmentId = ref<number | null>(null);

  function selectSegment(segmentId: number) {
    selectedSegmentId.value = segmentId;
  }

  function getNextViewId(segmentId: number | null) {
    if (!segmentId) return null;

    const selectedSegment = unrequiredSegments.value?.find((segment) => segment.id === segmentId);
    const firstViewGroup = selectedSegment?.view_groups?.[0];
    return firstViewGroup?.views?.[0]?.id || null;
  }

  function getNextGroupId(segmentId: number | null) {
    if (!segmentId) return null;

    const selectedSegment = unrequiredSegments.value?.find((segment) => segment.id === segmentId);
    const firstViewGroup = selectedSegment?.view_groups?.[0];
    return firstViewGroup?.id || null;
  }

  const selectedSegmentNextViewId = computed(() => getNextViewId(selectedSegmentId.value));

  const selectedSegmentNextGroupId = computed(() => getNextGroupId(selectedSegmentId.value));

  function addSegmentToCalculationIfNeeded(segmentId: number) {
    const segment = unrequiredSegments.value?.find((seg) => seg.id === segmentId);
    if (!segment || !currentCalculation.value) return;

    const segmentAlreadyExists = currentCalculation.value.calculation_segments.some(
      (seg) => seg.segment_id === segmentId
    );

    if (!segmentAlreadyExists) {
      calculationStore.addSegmentToCalculation(currentCalculation.value.uuid, segment);
    }
  }

  function navigateToNextView(segmentId: number, viewId: number | null, groupId: number | null) {
    if (!viewId) return;

    router.push({
      name: 'CalculationViewItems',
      params: {
        ...route.params,
        segmentId,
        viewId,
        groupId,
      },
    });
  }

  function handleNextView() {
    if (!selectedSegmentId.value) return;

    addSegmentToCalculationIfNeeded(selectedSegmentId.value);
    navigateToNextView(
      selectedSegmentId.value,
      selectedSegmentNextViewId.value,
      selectedSegmentNextGroupId.value
    );
  }

  return {
    selectedSegmentId,
    unrequiredSegments,
    selectedSegmentNextViewId,
    selectSegment,
    handleNextView,
  };
}
